import {inject, Injectable} from '@angular/core';
import {catchError, combineLatest, first, map, Observable, of, switchMap, tap,} from 'rxjs';
import {AuditService} from 'src/app/core/entities/audit/audit.service';
import {DeviceService} from 'src/app/core/entities/device/device.service';
import {IDevice} from 'src/app/core/entities/device/models/device.model';
import {Entry} from 'src/app/core/entities/entry/domain/model/entry.model';
import {IOrganization} from 'src/app/core/entities/organization/domain/interfaces/organization.interface';
import {
  OrganizationRepositoryAdapter
} from 'src/app/core/entities/organization/infrastructure/adapters/organization-repository.adapter';
import {ServicesService} from 'src/app/core/entities/service/services.service';
import {ITicketEvent} from 'src/app/core/entities/ticket-events/interface/ticket-event.model';
import {TicketEventService} from 'src/app/core/entities/ticket-events/ticket-event.service';
import {Logger} from 'src/app/core/interfaces/Logger';
import {IService} from 'src/app/core/interfaces/service.interface';
import {TrackingService} from 'src/app/core/services/tracking/tracking.service';
import {createAccountTracker} from 'src/app/core/services/tracking/utils/account-tracker.util';
import {AccountCreatedEvent} from '../../../../../shared/application/events/account/account-created.event';
import {EVENT_BUS} from "../../../../../shared/domain/tokens/event-bus.token";
import {AccountAuditHandlerService} from '../../account-audit-handler.service';
import {AccountModeService} from '../../account-modes/account-mode.service';
import {ACCOUNT_MODE} from '../../account-modes/enum/account-mode.enum';
import {Account} from '../../domain/model/account.model';
import {EntryMapper} from "../../../entry/infrastructure/mapper/entry.mapper";
import * as uuid from 'uuid';
import {AccountFactoryService} from "../../account-factory.service";

import { SERVICE_STATE_SERVICE } from '@features/service/domain/tokens/service-state.token';
import { Drawer } from '@features/service/domain/models/drawer.model';
import { Location } from '@features/service/domain/models/location.model';
import { Worker } from '@features/service/domain/models/worker.model';

interface IAccountCreationData {
  currentWorker: Worker | null;
  organization: IOrganization | null;
  device: IDevice | null;
  currentDrawer: Drawer | null;
  currentLocation: Location | null;
  service: IService | null;
  currentTicketEvent: ITicketEvent | null;
}

@Injectable({providedIn: 'root'})
export class AccountCreatorService {
  private readonly organizationService = inject(OrganizationRepositoryAdapter);
  private readonly deviceService = inject(DeviceService);
  private readonly serviceService = inject(ServicesService);
  private readonly ticketEventService = inject(TicketEventService);
  private readonly logger = inject(Logger);
  private readonly accountModeService = inject(AccountModeService);
  private readonly accountAuditHandler = inject(AccountAuditHandlerService);
  private readonly auditService = inject(AuditService);
  private readonly trackingService = inject(TrackingService);
  private readonly eventBus = inject(EVENT_BUS);
  private readonly factory = inject(AccountFactoryService);

  private readonly serviceStateService = inject(SERVICE_STATE_SERVICE);

  private readonly trackAccount = createAccountTracker(this.trackingService);

  execute(entries?: Entry[]): Observable<Account | undefined> {
    return this.collectAccountData().pipe(
      first(),
      switchMap((data) => this.processAccountCreation(data, entries)),
      catchError((error) => this.handleAccountCreationError(error))
    );
  }

  private processAccountCreation(
    data: IAccountCreationData,
    entries?: Entry[]
  ): Observable<Account> {
    this.logAccountCreationStart();

    return this.createNewAccount(data, entries).pipe(
      switchMap((account) => this.afterAccountCreated(account))
    );
  }

  private afterAccountCreated(account: Account): Observable<Account> {
    return combineLatest([
      this.resetAccountMode(),
      this.cleanupCurrentTicketEvent(),
      this.cleanupCurrentAudit(),
      this.auditCreateAccount(account),
    ]).pipe(
      map(() => account),
      tap((account) => this.trackAccount('Account created', account?.id)),
      tap((account) => this.eventBus.publish(new AccountCreatedEvent({account})))
    );
  }

  private auditCreateAccount(account: Account): Observable<unknown> {
    return this.accountAuditHandler
      .auditCreateAccountEvent(account)
      .pipe(catchError(() => of(null)));
  }

  private resetAccountMode(): Observable<ACCOUNT_MODE> {
    return this.accountModeService.insertAccountMode(ACCOUNT_MODE.default);
  }

  private cleanupCurrentTicketEvent(): Observable<unknown> {
    return this.ticketEventService.deleteCurrentTicketEvent();
  }

  private cleanupCurrentAudit(): Observable<unknown> {
    return this.auditService.removeCurrentAuditAccount();
  }

  private handleAccountCreationError(error: unknown): Observable<undefined> {
    this.logger.error(new Error('Error creating new account:'), error);

    return of(undefined);
  }

  private logAccountCreationStart(): void {
    this.logger.debug('[Account] [Create] Starting account creation...');
  }

  private createNewAccount(
    data: IAccountCreationData,
    entries?: Entry[]
  ): Observable<Account> {
      return this.factory.createOne({
        _id: uuid.v4(),
        cash_drawer_id: data.currentDrawer?.id || '',
        negocio_id: data.organization?._id || '',
        closed: false,
        waiter_id: data.currentWorker?.id || '',
        device_id: data.device?._id || '',
        service_id: data.service?._id || '',
        location_id: data.currentLocation?.id || '',
        created_at: Date.now(),
        note: '',
        entries: entries?.map(EntryMapper.toStorage) || [],
        trackId: uuid.v4(),
        products_quantity: 0,
        total: 0,
        payments_ids: [],
        refund_ids: [],
      }, true);
  }

  private collectAccountData(): Observable<IAccountCreationData> {
    return combineLatest({
      currentWorker: of(this.serviceStateService.getCurrentWorker()),
      organization: this.organizationService.getCurrentOrganization(),
      device: this.deviceService.getDevice(),
      currentDrawer: of(this.serviceStateService.getCurrentDrawer()),
      currentLocation: of(this.serviceStateService.getCurrentLocation() ?? null),
      service: this.serviceService.getCurrentService(),
      currentTicketEvent: this.ticketEventService.getCurrentTicketEvent(),
    }).pipe(
      map((data) => ({
        ...data,
        currentLocation: data.currentLocation
      }))
    );
  }
}
